import { usePermissions, useIsFreeTier, checkFeatureLimit } from '@nimbitlabs/features';
import { Navigate } from 'react-router-dom';

interface ProtectedPermissionRouteProps {
  element: React.ReactElement;
  permission?: string;
  permissions?: string[];
  requireAllPermissions?: boolean;
  requirePaidPlan?: boolean;
  requireFeature?: string; // Feature name to check limit (e.g., 'services', 'teams')
}

/**
 * Route protection component that enforces permission and subscription tier requirements
 * Redirects to /unauthorized if permission check fails
 * Redirects to /upgrade if paid plan is required but user is on free tier
 * Redirects to /upgrade if feature limit is reached (when requireFeature is specified)
 */
export function ProtectedPermissionRoute({
  element,
  permission,
  permissions,
  requireAllPermissions,
  requirePaidPlan,
  requireFeature,
}: ProtectedPermissionRouteProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  const isFreeTier = useIsFreeTier();
  
  // Check tier requirement
  if (requirePaidPlan && isFreeTier) {
    return <Navigate to="/upgrade" replace />;
  }
  
  // Check feature limit
  if (requireFeature && checkFeatureLimit(requireFeature)) {
    return <Navigate to="/upgrade" replace />;
  }
  
  // Check single permission
  if (permission && !hasPermission(permission)) {
    return <Navigate to="/unauthorized" replace />;
  }
  
  // Check multiple permissions
  if (permissions) {
    const checkFn = requireAllPermissions ? hasAllPermissions : hasAnyPermission;
    if (!checkFn(permissions)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }
  
  return element;
}
